import React, { useEffect, useState } from 'react';
/**
 * `TextInput` is a reusable text entry component.
 *
 * Behaviour:
 * - Trims whitespace on blur and emits the trimmed value via `onChange`.
 * - Optional `handleReturn` triggers the blur-style trim when Enter is pressed.
 * - Supports readonly/disabled mode.
 * - When `inputType` is `password`, provides a show/hide toggle button and can
 *   reset visibility via `visibilityResetKey`.
 */
import '../validation.scss';
import './TextInput.scss';
import InputLabel from '../InputLabel/InputLabel';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

export type TextInputProps = {
  name: string;
  className?: string;
  labelClassName?: string;
  ariaLabel?: string;
  error?: string;
  placeholder?: string;
  label?: string;
  value?: string;
  dataKey?: string;
  maxLength?: number;
  readOnly?: boolean;
  autoFocus?: boolean;
  focus?: boolean;
  handleReturn?: boolean;
  stopPropagationOnClick?: boolean;
  ariaDescribeLink?: string;
  inputType?: string;
  visibilityResetKey?: number | string;
  children?: React.ReactNode;
  onFocus?: (event: React.FormEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
  onLeave?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
  hasError?: boolean;
};

/** A generic text input that is used as an all purpose data entry box */
const TextInput = (props: TextInputProps) => {
  const {
    name,
    placeholder,
    ariaLabel,
    value,
    dataKey = '',
    maxLength = 10000,
    className,
    labelClassName,
    stopPropagationOnClick,
    ariaDescribeLink = '',
    handleReturn,
    autoFocus,
    label,
    error,
    readOnly,
    focus,
    visibilityResetKey,
    onChange,
    onLeave,
    onKeyPress,
    onFocus,
    children,
    inputType,
    hasError
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const isAriaDisabled = !!readOnly;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isPasswordField = inputType === 'password';

  useEffect(() => {
    if (isPasswordField && visibilityResetKey) {
      setShowPassword(false);
    }
  }, [isPasswordField, visibilityResetKey]);

  // add a handler to trim the values on leave ...
  const onLeaveInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (isAriaDisabled) {
      return;
    }

    let val = event.currentTarget.value;

    if (typeof val === 'string') {
      val = val.trim();
      event.currentTarget.value = val;

      onChange && onChange(event, dataKey);
    }

    if (onLeave !== undefined) {
      onLeave(event, dataKey);
    }
  };

  const onClickInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (stopPropagationOnClick) {
      event.stopPropagation();
    }
  };

  const onKeyPressed = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (handleReturn) {
      if (event.key === 'Enter' && !isAriaDisabled) {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const blurEvent: any = { currentTarget: { value: null } };
        onLeaveInput(blurEvent); //eslint-disable-line
      }
    }

    if (onKeyPress !== undefined) {
      onKeyPress(event);
    }
  };

  let styleWrapper = 'form-group';
  if (hasError || !CommonValidation.isEmpty(error)) {
    styleWrapper += ' has-error';
  }

  let baseClasses = 'field ';
  if (className) {
    baseClasses += className;
  }

  const onChangeInput = (
    event: React.FormEvent<HTMLInputElement>,
    key: string
  ) => {
    onChange && onChange(event, key);
  };

  const fieldClassName = `${children ? 'field input-group' : baseClasses}${isPasswordField ? ' has-password-toggle' : ''}`;

  return (
    <div className={styleWrapper}>
      {label && (
        <InputLabel className={labelClassName} labelText={label} name={name} />
      )}
      <div className={fieldClassName}>
        <input
          id={name}
          name={name}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribeLink}
          aria-disabled={isAriaDisabled || undefined}
          type={isPasswordField && showPassword ? 'text' : inputType ?? 'text'}
          autoComplete='off'
          className='form-control'
          placeholder={readOnly ? '' : placeholder}
          autoFocus={autoFocus}
          value={value}
          onKeyPress={onKeyPressed}
          onChange={(e) => onChangeInput(e, dataKey)}
          onFocus={onFocus}
          onBlur={onLeaveInput}
          onClick={onClickInput}
          maxLength={maxLength}
          readOnly={readOnly}
          ref={(input) => input && focus && input.focus()}
        />
        {isPasswordField && (
          <button
            type='button'
            className='toggle-password'
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? 'Hide password' : 'Show password'}>
            <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
          </button>
        )}
        {children}
      </div>
      <div className='input'>{error}</div>
    </div>
  );
};

export default TextInput;
